import { Component } from '@theme/component';

/**
 * Announcement banner custom element.
 *
 * Supports directional vertical transitions:
 * - Next / ↓ : new announcement enters from bottom
 * - Previous / ↑ : new announcement enters from top
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * Direction of the current transition.
   *
   * 'next' = bottom to top
   * 'previous' = top to bottom
   *
   * @type {'next'|'previous'}
   */
  #direction = 'next';

  /**
   * Interval ID for automatic playback.
   *
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);

    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.current = 0;

    this.play();
  }

  next() {
    this.#direction = 'next';
    this.current += 1;
  }

  previous() {
    this.#direction = 'previous';
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   *
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic playback.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];

    if (!slides.length) return;

    const previousIndex = this.#current;

    this.#current = current;

    let relativeIndex = current % slides.length;

    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    /*
     * Remove old directional classes.
     */
    this.classList.remove(
      'announcement-transition-next',
      'announcement-transition-previous'
    );

    /*
     * Add the correct direction.
     */
    this.classList.add(
      this.#direction === 'previous'
        ? 'announcement-transition-previous'
        : 'announcement-transition-next'
    );

    /*
     * Force a reflow so that repeated clicks
     * trigger the animation correctly.
     */
    void this.offsetWidth;

    slides.forEach((slide, index) => {
      const isActive = index === relativeIndex;

      slide.setAttribute(
        'aria-hidden',
        `${!isActive}`
      );

      slide.classList.toggle(
        'announcement-slide-active',
        isActive
      );

      slide.classList.toggle(
        'announcement-slide-inactive',
        !isActive
      );
    });

    /*
     * Store the previous index for the CSS state.
     */
    this.dataset.previousIndex = `${previousIndex}`;
    this.dataset.currentIndex = `${relativeIndex}`;
  }

  /**
   * Pause slideshow when page becomes hidden.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define(
    'announcement-bar-component',
    AnnouncementBar
  );
}